import { useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Send, CheckCircle2 } from "lucide-react";
import { validateContactForm } from "@/lib/validations/contactForm";
import { HoneypotField, isHoneypotFilled } from "@/components/forms/HoneypotField";
import { useRateLimiter } from "@/hooks/useRateLimiter";
import { AIAgentCard } from "@/components/ai";
import { CountryCombobox } from "@/components/forms/CountryCombobox";
import { TreatmentSelector } from "@/components/forms/TreatmentSelector";
import { ContactInfoCard } from "@/components/forms/ContactInfoCard";
import { SimpleCombobox } from "@/components/forms/SimpleCombobox";
import { PageHeader } from "@/components/shared/PageHeader";
import { OrganizationJsonLd, SEOHead } from "@/components/seo";
import { SEO_METADATA } from "@/lib/seo-metadata";
import { COMPANY } from "@/config/company";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

const TRAVEL_TIMEFRAME_OPTIONS = [
  { value: "within_1_month", label: "Within 1 month" },
  { value: "1_3_months", label: "1–3 months" },
  { value: "3_6_months", label: "3–6 months" },
  { value: "6_plus_months", label: "6+ months" },
  { value: "not_sure", label: "Not sure yet" },
] as const;

const URGENCY_OPTIONS = [
  { value: "immediate", label: "Immediate – ASAP" },
  { value: "within_week", label: "Within a week" },
  { value: "within_month", label: "Within a month" },
  { value: "flexible", label: "Flexible / No rush" },
] as const;

export default function Contact() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState("");
  const { isRateLimited, checkRateLimit, remainingRequests } = useRateLimiter({ 
    maxRequests: 5, 
    windowMs: 300000 // 5 minutes
  });
  const { trackFormStart, trackFormSubmit } = useAnalyticsContext();
  const formStartTracked = useRef(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
  });
  
  // Treatment selection state
  const [treatmentCategory, setTreatmentCategory] = useState("");
  const [treatmentSubcategory, setTreatmentSubcategory] = useState("");
  const [otherCategory, setOtherCategory] = useState("");
  const [otherSubcategory, setOtherSubcategory] = useState("");
  
  // Travel planning state
  const [travelTimeframe, setTravelTimeframe] = useState("");
  const [urgency, setUrgency] = useState("");

  const updateFormField = useCallback((field: string, value: string) => {
    // Track form start on first interaction
    if (!formStartTracked.current) {
      trackFormStart('contact_form', 'contact_page');
      formStartTracked.current = true;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  }, [formErrors, trackFormStart]);

  // Build treatment interest string
  const getTreatmentInterest = () => {
    if (treatmentCategory === "other") {
      return otherCategory || "Other";
    }
    
    let interest = treatmentCategory;
    
    if (treatmentSubcategory === "other") {
      interest += ` - ${otherSubcategory || "Other"}`;
    } else if (treatmentSubcategory) {
      interest += ` - ${treatmentSubcategory}`;
    }
    
    return interest;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    
    // Check honeypot (bot detection)
    if (isHoneypotFilled(honeypot)) {
      toast({
        title: "Inquiry Submitted!",
        description: "Our team will contact you within 24 hours.",
      });
      return;
    }
    
    // Check rate limit
    if (checkRateLimit()) {
      toast({
        variant: "destructive",
        title: "Too Many Requests",
        description: `Please wait a few minutes before submitting again. (${remainingRequests} submissions remaining)`,
      });
      return;
    }
    
    const treatmentInterest = getTreatmentInterest();
    
    // Validate form data
    const validation = validateContactForm({
      ...formData,
      treatment_interest: treatmentInterest,
      travel_timeframe: travelTimeframe,
      urgency: urgency,
    });
    
    if (!validation.success) {
      setFormErrors(validation.errors || {});
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please correct the errors in the form.",
      });
      return;
    }
    
    setLoading(true);

    const { error } = await supabase.from("inquiries").insert([
      {
        name: validation.data!.name,
        email: validation.data!.email,
        phone: validation.data!.phone || null,
        country: validation.data!.country || null,
        treatment_interest: treatmentInterest || null,
        travel_timeframe: travelTimeframe || null,
        urgency: urgency || null,
        message: validation.data!.message,
        source: "contact_page",
      },
    ]);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit inquiry. Please try again.",
      });
    } else {
      setSubmitted(true);
      trackFormSubmit('contact_form', true, treatmentInterest);
      toast({
        title: "Inquiry Submitted!",
        description: "Our team will contact you within 24 hours.",
      });
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      country: "",
      message: "",
    });
    setTreatmentCategory("");
    setTreatmentSubcategory("");
    setOtherCategory("");
    setOtherSubcategory("");
    setTravelTimeframe("");
    setUrgency("");
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={SEO_METADATA.contact.title}
        description={SEO_METADATA.contact.description}
        canonicalPath="/contact/"
        keywords={SEO_METADATA.contact.keywords}
      />
      <OrganizationJsonLd />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <PageHeader 
            title="Get in Touch"
            description="Have questions about your medical journey? Our team is here to help 24/7"
          />

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info & AI Assistant */}
            <div className="lg:col-span-1 space-y-6">
              {/* AI Assistant - Prioritized for immediate help */}
              <AIAgentCard 
                context="getting in touch with TrustedHeal"
                title="Talk to Dr. Maya Now"
                description="Get instant answers about treatments, costs, and next steps. No waiting required!"
                quickPrompts={[
                  "Get a free quote",
                  "What treatment is right for me?",
                  "Start my journey"
                ]}
                compact
              />

              <ContactInfoCard
                type="phone"
                title="Call Us"
                subtitle="Available 24/7 for your queries"
                value={COMPANY.phone.display}
              />

              <ContactInfoCard
                type="whatsapp"
                title="WhatsApp"
                subtitle="Quick responses anytime"
                value={COMPANY.phone.displayFull}
              />

              <ContactInfoCard
                type="email"
                title="Email Us"
                subtitle="We reply within 24 hours"
                value={COMPANY.email.info}
              />

              <ContactInfoCard
                type="location"
                title="Office"
                subtitle="Visit us in person"
                value={COMPANY.address.full}
              />
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2 min-w-0">
              <Card className="border-0 shadow-soft overflow-hidden">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-secondary">
                    Send Us a Message
                  </CardTitle>
                  <p className="text-muted-foreground">
                    Fill out the form below and we'll get back to you within 24 hours
                  </p>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="font-display text-2xl font-semibold text-secondary mb-2">
                        Thank You!
                      </h3>
                      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        Your inquiry has been submitted successfully. Our patient care team will contact you within 24 hours.
                      </p>
                      <Button onClick={resetForm} variant="outline">
                        Submit Another Inquiry
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Honeypot field for bot detection */}
                      <HoneypotField value={honeypot} onChange={setHoneypot} />
                      
                      {/* Row 1: Name & Email */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            required
                            placeholder="John Doe"
                            maxLength={200}
                            autoComplete="name"
                            value={formData.name}
                            onChange={(e) => updateFormField("name", e.target.value)}
                            className={formErrors.name ? "border-destructive" : ""}
                          />
                          {formErrors.name && (
                            <p className="text-sm text-destructive">{formErrors.name}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            required
                            placeholder="john@example.com"
                            maxLength={255}
                            autoComplete="email"
                            value={formData.email}
                            onChange={(e) => updateFormField("email", e.target.value)}
                            className={formErrors.email ? "border-destructive" : ""}
                          />
                          {formErrors.email && (
                            <p className="text-sm text-destructive">{formErrors.email}</p>
                          )}
                        </div>
                      </div>

                      {/* Row 2: Phone & Country */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number *</Label>
                          <Input
                            id="phone"
                            type="tel"
                            required
                            placeholder="+1 234 567 8900"
                            maxLength={50}
                            autoComplete="tel"
                            value={formData.phone}
                            onChange={(e) => updateFormField("phone", e.target.value)}
                            className={formErrors.phone ? "border-destructive" : ""}
                          />
                          {formErrors.phone && (
                            <p className="text-sm text-destructive">{formErrors.phone}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Country *</Label>
                          <CountryCombobox
                            value={formData.country}
                            onValueChange={(value) => updateFormField("country", value)}
                            error={formErrors.country}
                          />
                          {formErrors.country && (
                            <p className="text-sm text-destructive">{formErrors.country}</p>
                          )}
                        </div>
                      </div>

                      {/* Treatment Selection with TanStack */}
                      <div className="space-y-2">
                        <TreatmentSelector
                          category={treatmentCategory}
                          subcategory={treatmentSubcategory}
                          onCategoryChange={setTreatmentCategory}
                          onSubcategoryChange={setTreatmentSubcategory}
                          otherCategory={otherCategory}
                          otherSubcategory={otherSubcategory}
                          onOtherCategoryChange={setOtherCategory}
                          onOtherSubcategoryChange={setOtherSubcategory}
                          required
                          error={formErrors.treatment_interest}
                        />
                      </div>

                      {/* Travel Planning Row */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="travelTimeframe">When are you planning to travel? *</Label>
                          <SimpleCombobox
                            id="travelTimeframe"
                            placeholder="Select timeframe"
                            searchPlaceholder="Search timeframe..."
                            value={travelTimeframe}
                            onValueChange={setTravelTimeframe}
                            options={[...TRAVEL_TIMEFRAME_OPTIONS]}
                            error={formErrors.travel_timeframe}
                          />
                          {formErrors.travel_timeframe && (
                            <p className="text-sm text-destructive">{formErrors.travel_timeframe}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="urgency">How urgent is your booking? *</Label>
                          <SimpleCombobox
                            id="urgency"
                            placeholder="Select urgency"
                            searchPlaceholder="Search urgency..."
                            value={urgency}
                            onValueChange={setUrgency}
                            options={[...URGENCY_OPTIONS]}
                            error={formErrors.urgency}
                          />
                          {formErrors.urgency && (
                            <p className="text-sm text-destructive">{formErrors.urgency}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Your Message (Optional)</Label>
                        <Textarea
                          id="message"
                          placeholder="Tell us about your medical needs, preferred dates, or any questions you have..."
                          rows={4}
                          maxLength={5000}
                          value={formData.message}
                          onChange={(e) => updateFormField("message", e.target.value)}
                        />
                        <div className="flex justify-end text-sm text-muted-foreground">
                          <span>{formData.message.length}/5000</span>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        className="w-full rounded-full"
                        size="lg"
                        disabled={loading}
                      >
                        {loading ? (
                          "Submitting..."
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Submit Inquiry
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-muted-foreground text-center">
                        By submitting, you agree to our <Link to="/privacy-policy/" className="underline hover:text-primary">Privacy Policy</Link> and <Link to="/terms-of-service/" className="underline hover:text-primary">Terms of Service</Link>. Your information is secure and will never be shared.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          {/* SEO Content */}
          <div className="mt-16 max-w-4xl mx-auto text-center border-t pt-12">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-secondary mb-4">
              Your Trusted Partner in Medical Tourism
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">
              At TrustedHeal, we understand that traveling abroad for medical treatment is a significant decision. Our dedicated patient care team is committed to providing transparent, comprehensive, and compassionate support at every step of your journey. Whether you're exploring affordable treatment options in India, seeking a second medical opinion, or need assistance with medical visa processing and travel logistics, we are here to assist. 
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed">
              We partner exclusively with JCI and NABH accredited hospitals to ensure you receive world-class care from internationally trained doctors. Reach out to us today for a free, no-obligation consultation, and let us help you achieve your health goals safely and affordably.
            </p>
          </div>
        </div>
      </main>

    </div>
  );
}
